/* eslint-disable */
import {
	useState, useEffect, useMemo, useCallback
} from 'react';
import {
	meals,
	parentTabs,
	tabs,
	tableTabsIssuedSummary,
	tableTabsProducedSummary,
	tableTabsDispatchedSummary,
	tableTabsOverView,
	tableTabsPackagedSummary,
	tableTabsSecondaryPackagedSummary,
	tableTabsWastageSummary,
	nagaIssuedSummaryTabs,
	nagaProducedSummaryTabs,
	tableTabsDispatchedSummaryNaga,
	dashboardTabs,
	processTabsByParent,
} from '../constants';
import { useSelector } from 'react-redux';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';

// Distribution process formats that exist only for the Dispatch View (no Receive View):
// FIFO is checked only when the kitchen dispatches; Order Placement is per outlet order, not receiving.
const DISPATCH_ONLY_FORMATS = ['fifoAdherence', 'orderPlacement'];
// Distribution table tabs that exist only for the D&R Time format.
const DR_TIME_ONLY_TABS = ['overview'];

// Map of table tab names to their arrays
const tableTabsMap = {
	tableTabsIssuedSummary,
	tableTabsProducedSummary,
	tableTabsDispatchedSummary,
	tableTabsOverView,
	tableTabsPackagedSummary,
	tableTabsSecondaryPackagedSummary,
	tableTabsWastageSummary,
	nagaIssuedSummaryTabs,
	nagaProducedSummaryTabs,
	tableTabsDispatchedSummaryNaga,
};

export const useTableState = (rawData, setSearch, isNagaCustomer = false, isDanaChogaCustomer = false, parentTabList, kitchenId = null) => {
	const [parentTab, setParentTab] = useState(parentTabList[0]);
	const [active, setActive] = useState(null);
	const [selectedTab, setSelectedTab] = useState(tabs[0]?.id);
	const [selectedDashboard, setSelectedDashboard] = useState(dashboardTabs[0]?.id);
	const [operationalDataView, setOperationalDataView] = useState('inventory');
	const [selectSubTab, setSelectSubTab] = useState(tabs.find(e => e.id === selectedTab)?.subTabs?.[0]?.id)
	const [perUnit, setPerUnit] = useState(false)
	const [tableTab, setTableTab] = useState('');
	const [mealTabsList, setMealTabsList] = useState([]);
	const shiftList = useSelector((state) => state?.analytics?.SHIFT_LIST);
	// Shifts are configured per store, so the tab strip has to follow the Kitchen
	// picked in the filter drawer instead of the customer-wide SHIFT_LIST that
	// redux holds. `null` means "no kitchen-scoped list yet", which is what makes
	// the shared list the fallback rather than showing an empty strip.
	const [kitchenShifts, setKitchenShifts] = useState(null);
	const [parentTabVersion, setParentTabVersion] = useState(0);

	const getTableTabsForParent = useCallback((tab) => {
		if (!tab) return [];
		if (tab.tableTabsNaga) {
			return tableTabsMap[tab.tableTabsNaga] || [];
		}
		if (tab.tableTabs) {
			return tableTabsMap[tab.tableTabs] || [];
		}

		return [];
	}, [isNagaCustomer, isDanaChogaCustomer]);

	useEffect(() => {
		let cancelled = false;
		if (!kitchenId || kitchenId === -999) {
			setKitchenShifts(null);
			return () => { };
		}
		(async () => {
			try {
				const response = await inventoryAnalyticsServices?.fetchShifts({
					params: { storeId: kitchenId },
				});
				if (cancelled) return;
				setKitchenShifts(
					response?.statusCode === 200 ? (response?.data ?? []) : []
				);
			} catch (error) {
				// A failed lookup must not leave the page unusable - 'All' is still
				// offered, which is the unfiltered view anyway.
				console.error(error);
				if (!cancelled) setKitchenShifts([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [kitchenId]);

	useEffect(() => {
		setMealTabsList([{ id: -999, shiftName: 'All' }, ...(kitchenShifts ?? shiftList ?? [])]);
	}, [kitchenShifts, shiftList]);

	useEffect(() => {
		if (mealTabsList?.length > 0) {
			setActive(mealTabsList[0]);
		}
	}, [mealTabsList]);

	useEffect(() => {
		// D&R-only tabs (Overview) are listed first but are never the landing tab.
		const tabList = getTableTabsForParent(parentTab)?.filter((t) => !DR_TIME_ONLY_TABS.includes(t?.value));
		if (tabList?.length > 0) {
			// In Process Operational Data mode, land on the first tab that
			// is explicitly opted-in via `processOper: true`. Most parents use
			// processWise, but parents without one (e.g., Distribution) flag
			// their own view (Outlets / Dispatch / Receive).
			if (operationalDataView === 'processAnalysis') {
				const firstProcessOper = tabList.find((t) => t?.processOper === true);
				setTableTab(firstProcessOper?.value || tabList[0]?.value);
			} else {
				setTableTab(tabList[0]?.value);
			}
			setParentTabVersion((prev) => prev + 1);
		}
	}, [parentTab, getTableTabsForParent, operationalDataView]);

	// When user switches to Process Operational Data view, force the inner
	// table tab to a processOper-flagged tab (if the current one isn't).
	// When switching back to inventory view, leave the current tab as-is so
	// user keeps their position.
	useEffect(() => {
		if (operationalDataView !== 'processAnalysis') return;
		const tabList = getTableTabsForParent(parentTab);
		if (DR_TIME_ONLY_TABS.includes(tableTab)) return;
		const currentIsProcessOper = tabList?.some(
			(t) => t?.value === tableTab && t?.processOper === true
		);
		if (currentIsProcessOper) return;
		const firstProcessOper = tabList?.find((t) => t?.processOper === true && !DR_TIME_ONLY_TABS.includes(t?.value));
		if (firstProcessOper) {
			setTableTab(firstProcessOper.value);
			setParentTabVersion((prev) => prev + 1);
		}
	}, [operationalDataView, parentTab, tableTab, getTableTabsForParent]);

	const data = useMemo(() => {
		if (!rawData?.length || !active) return {};
		const currentData = rawData.find((item) => item?.mealType === active?.value) || {};
		return currentData;
	}, [rawData, active]);

	const handleParentTab = useCallback((tab) => {
		setParentTab(tab);
	}, []);

	const handleMealTab = useCallback((tab) => {
		setActive(tab);
	}, []);

	const handleTableTab = useCallback((value) => {
		setSearch({});
		// if (value !== 'rawMaterials') {
		// 	setSelectedTab('weight');
		// }
		setTableTab(value);
		setParentTabVersion((prev) => prev + 1);
	}, []);

	// FIFO Adherence on Distribution: FIFO is checked only when the kitchen dispatches, so there
	// is no Receive View for it. If Receive was open when the user picks FIFO, go back to Dispatch.
	const isDispatchOnlyFormat = operationalDataView === 'processAnalysis'
		&& parentTab?.value === 'DISPATCHED'
		&& DISPATCH_ONLY_FORMATS.includes(selectedTab);

	useEffect(() => {
		if (isDispatchOnlyFormat && tableTab === 'receive') {
			setTableTab('outlets');
			setParentTabVersion((prev) => prev + 1);
		}
	}, [isDispatchOnlyFormat, tableTab]);

	// Overview exists only on Process Data → Distribution → D&R Time. Leaving D&R Time while it
	// is open drops back to the Dispatch View.
	const isDrTimeFormat = operationalDataView === 'processAnalysis'
		&& parentTab?.value === 'DISPATCHED'
		&& selectedTab === 'drTime';

	useEffect(() => {
		if (!isDrTimeFormat && DR_TIME_ONLY_TABS.includes(tableTab)) {
			setTableTab('outlets');
			setParentTabVersion((prev) => prev + 1);
		}
	}, [isDrTimeFormat, tableTab]);

	const getTabInfo = useCallback(() => {
		let tabList = getTableTabsForParent(parentTab);
		// In Process Operational Data mode, restrict the inner table-tab
		// section to tabs explicitly opted-in via `processOper: true`.
		// Each parent decides which of its views are relevant (most use
		// processWise; Distribution opts in Outlets / Dispatch / Receive).
		if (operationalDataView === 'processAnalysis') {
			tabList = (tabList ?? []).filter((t) => t?.processOper === true);
		}
		if (isDispatchOnlyFormat) {
			tabList = (tabList ?? []).filter((t) => t?.value !== 'receive');
		}
		if (!isDrTimeFormat) {
			tabList = (tabList ?? []).filter((t) => !DR_TIME_ONLY_TABS.includes(t?.value));
		}
		// Order Placement is one per-outlet list — no Dispatch / Receive views to switch between.
		if (operationalDataView === 'processAnalysis' && parentTab?.value === 'DISPATCHED' && selectedTab === 'orderPlacement') {
			tabList = [];
		}

		return {
			tabList: tabList,
			activeTabColor: 'bg-yellow-200',
			tabValue: tableTab,
			handleTabChange: handleTableTab,
		};
	}, [parentTab, tableTab, handleTableTab, getTableTabsForParent, operationalDataView, isDispatchOnlyFormat, isDrTimeFormat, selectedTab]);

	const getAvailableTabs = useCallback(() => {
		if (operationalDataView === 'processAnalysis') {
			const processTabs = processTabsByParent[parentTab?.value] || [];
			return processTabs.map((tab) => ({ ...tab }));
		}

		let currentTabs = tabs;
		if ((parentTab?.value === 'ISSUED' && tableTab === 'rawMaterials') ||
			(parentTab?.value === 'PRODUCED' && tableTab === 'products')) {
			currentTabs = tabs;
		} else {
			currentTabs = tabs.filter((tab) => tab.id !== 'percentage');
		}

		return currentTabs.map((tab) => {
			let disabled = false;
			let tooltip = '';

			if (tab.id === 'cost' && parentTab?.value === 'PRODUCED') {
				disabled = true;
				tooltip = 'Cost Tab is not Available for Produced';
			}

			if (tab.id === 'cost' && !['ISSUED', 'DISPATCHED'].includes(parentTab?.value) && selectedDashboard === 'plannedVsActual') {
				disabled = true;
				tooltip = 'Cost Tab is not Available for Planned vs Actual';
			}

			if (tab.id === 'qrCount' && selectedDashboard === 'batchPerformance') {
				const restrictedParents = ['ISSUED', 'OVERVIEW', 'WASTAGE'];
				if (restrictedParents.includes(parentTab?.value)) {
					disabled = true;
					tooltip = `QR Counts tab is not Available for ${parentTab.header} in Batch Performance`;
				}
			}

			return {
				...tab,
				...(disabled && { disabled, tooltip }),
			};
		});
	}, [parentTab, tableTab, selectedDashboard, operationalDataView]);

	useEffect(() => {
		const availableTabs = getAvailableTabs();
		if (!availableTabs?.length) return;
		const currentTab = availableTabs.find((tab) => tab.id === selectedTab);
		if (!currentTab || currentTab.disabled) {
			const firstEnabled = availableTabs.find((t) => !t.disabled) || availableTabs[0];
			if (firstEnabled?.id && firstEnabled.id !== selectedTab) {
				setSelectedTab(firstEnabled.id);
			}
		}
	}, [selectedDashboard, parentTab, tableTab, getAvailableTabs, selectedTab, operationalDataView]);

	// Batch Performance mode disables the Distribution (DISPATCHED) parent tab
	// (see ParentTabsSection). If Distribution is the active tab when batch mode
	// turns on, fall back to the Issued tab so the user isn't stranded on a
	// disabled/empty tab. Changing parentTab cascades a refetch via parentTabVersion.
	useEffect(() => {
		if (selectedDashboard === 'batchPerformance' && parentTab?.value === 'DISPATCHED') {
			const issuedTab = parentTabList?.find((t) => t?.value === 'ISSUED') || parentTabList?.[0];
			if (issuedTab && issuedTab.value !== parentTab?.value) {
				setParentTab(issuedTab);
			}
		}
	}, [selectedDashboard, parentTab, parentTabList]);

	return {
		parentTab,
		active,
		selectedTab,
		tableTab,
		mealTabsList,
		data,
		setSelectedTab,
		handleParentTab,
		handleMealTab,
		handleTableTab,
		getTabInfo,
		getAvailableTabs,
		parentTabVersion,
		selectSubTab,
		setSelectSubTab,
		perUnit,
		setPerUnit,
		selectedDashboard,
		setSelectedDashboard,
		setParentTab,
		operationalDataView,
		setOperationalDataView,
	};
};
